import React, { useEffect, useState } from "react"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import { TableBody, TableRow, styled, Stack, Typography } from "@mui/material"
import { PETextField } from "../StyledPackagingExecutionPreviewComponents"
import { StyledTable, StyledBodyCell, StyledHeaderCell, StyledFooterCell } from "../../../components/styled/StyledTableComponents"
import { getPseWithSectionOutputs } from "../../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"

import { COLORS, roundNumber } from "../../../utils"

// form id to be used to submit the form outside the form component
export const PESectionsWasteFormId = "PESectionsWasteFormId"

const renderWaste = (waste = 0) => roundNumber(waste, 2)

const getSectionsTotalWeight = (sections = [], key) => {
	return renderWaste(sections.reduce((acc, section) => acc += (+section[key] || 0), 0))
}

const schema = Yup.object().shape({
	sections: Yup.array().of(
		Yup.object().shape({
			realWaste: Yup.number().required("Champ obligatoire")
		})
	)
})

const StyledUnit = styled("span")({
	fontSize: 16,
	fontStyle: "normal",
	fontWeight: 400,
	lineHeight: 1.5,
	color: COLORS.BLACK_500,
})

const StyledValue = styled("span")({
	color: "#000",
})

const PESectionsWasteForm = ({ sections = [], handleSubmit, isEdition = true }) => {
	const [localSections, setLocalSections] = useState(sections)

	useEffect(() => {
		(async () => {
			// Keep only unused sectionOutputs if exist - adjust weights for distributed outputs
			const currentSections = await Promise.all(sections.map(async section => {
				const outputs = section.section?.sectionOutputs
				if (!outputs || outputs.length === 0) return section
				
				const outputPercentages = await Promise.all(outputs.map(async output => {
					const pse = await getPseWithSectionOutputs([output])
					// If output is used elsewhere, it doesn't stay in this section (percentage = 0)
					// If output is NOT used elsewhere, it stays in this section (percentage = calculated)
					if (pse) return { percentage: 0, name: output.name ?? `${output.percentage} %` }
					const totalNetWeight = outputs.reduce((acc, output) => acc + (output.ingredients ?? []).reduce((acc, ingredient) => acc + ingredient.netWeight, 0), 0)
					return {
						percentage: output.percentage ? output.percentage : (output.ingredients.reduce((acc, ingredient) => acc + ingredient.netWeight, 0) / totalNetWeight * 100),
						name: output.name ?? `${output.percentage} %`
					}
				})).then(percentages => {
					const totalPercentage = percentages.reduce((acc, output) => acc + output.percentage, 0)
					const names = percentages.filter(output => output.percentage).map(output => output.name).join(", ")
					return {
						outputPercentages: percentages,
						totalPercentage,
						names
					}
				})

				// If totalPercentage is 0, ALL outputs are used elsewhere - waste should be 0
				if (outputPercentages.totalPercentage === 0) {
					return {
						...section,
						forecastWaste: 0,
						realWaste: 0,
						sectionName: section.sectionName + " - (Totalement répartie)",
					}
				}

				// If totalPercentage is 100, NO outputs are used elsewhere - return section unchanged
				if (outputPercentages.totalPercentage === 100) return section

				// If totalPercentage is between 0 and 100, only part of the section stays here
				// Adjust waste values proportionally to the section output percentage
				return {
					...section,
					...["forecastWaste", "realWaste"].reduce((acc, key) => {
						if (section[key])
							acc[key] = section[key] * outputPercentages.totalPercentage / 100
						return acc
					}, {}),
					sectionName: section.sectionName + " - " + outputPercentages.names,
				}
			}))
			setLocalSections(currentSections)
		})()
	}, [sections])

	const headerLabels = ["Éléments à barquetter", "Restes prévus", "Restes pesés"]
	return (
		<Formik
			initialValues={{
				sections: localSections.map(section => ({
					...section,
					realWaste: section.realWaste ?? section.forecastWaste ?? ""
				}))
			}}
			onSubmit={handleSubmit}
			validationSchema={schema}
			enableReinitialize={true}
		>
			{({ values, handleChange, errors, touched }) => (
				<Form id={PESectionsWasteFormId}>
					<StyledTable>
						<TableRow>
							{headerLabels.map((label, index) => (
								<StyledHeaderCell key={index}>{label}</StyledHeaderCell>
							))}
						</TableRow>

						<TableBody>
							{values.sections.map((section, index) => (
								<TableRow key={index}>
									<StyledBodyCell leftEdge={true} lastBodyRow={index === values.sections.length - 1}>
										{section.sectionName}
									</StyledBodyCell>
									<StyledBodyCell lastBodyRow={index === values.sections.length - 1}>
										<StyledValue>{renderWaste(section.forecastWaste)}</StyledValue> <StyledUnit>kg</StyledUnit>
									</StyledBodyCell>
									<StyledBodyCell rightEdge={true} lastBodyRow={index === values.sections.length - 1}>
										<Stack direction="row" justifyContent="start" alignItems="center" spacing={1}>
											{isEdition
												?  (
													<PETextField
														onChange={handleChange}
														name={`sections[${index}].realWaste`}
														variant="outlined"
														error={errors?.sections && touched.sections && touched?.sections[index]?.realWaste && errors.sections[index]?.realWaste}
														disabled={!isEdition}
														value={section.realWaste}
														type="number"
														inputProps={{
															inputMode: "numeric",
															pattern: "[0-9]*",
														}}
													/>
												)
												: roundNumber(section.forecastWaste, 2)}
											<StyledUnit> kg</StyledUnit>
										</Stack>
										{errors?.sections && touched?.sections && touched?.sections[index]?.realWaste && errors.sections[index]?.realWaste && (
											<Typography variant="body3" color="error"	>
												{errors.sections[index]?.realWaste}
											</Typography>
										)}
									</StyledBodyCell>
								</TableRow>
							))}
							<TableRow>
								<StyledFooterCell>Total</StyledFooterCell>
								<StyledFooterCell>
									{getSectionsTotalWeight(values.sections, "forecastWaste") || 0} kg
								</StyledFooterCell>
								<StyledFooterCell>
									{getSectionsTotalWeight(values.sections, "realWaste") || 0} kg
								</StyledFooterCell>
							</TableRow>
						</TableBody>
					</StyledTable>
				</Form>
			)}
		</Formik>
	)

}

export default PESectionsWasteForm